<template>
  <div class="search-container">
    <input
      ref="searchInput"
      type="text"
      v-model="searchQuery"
      @input="searchTrainees"
      @keydown="handleKeydown"
      placeholder="البحث عن متدرب..."
      class="form-control mb-3"
      dir="rtl"
    />
    <ul 
      v-if="trainees.length > 0" 
      class="list-group"
      @scroll="handleScroll"
      ref="traineesList"
    >
      <li
        v-for="(trainee, index) in trainees"
        :key="trainee.id"
        :class="['list-group-item', { 'active-item': index === selectedIndex }]"
        @mouseenter="selectedIndex = index"
        @click="navigateToTrainee(trainee.id)"
      >
        <router-link :to="{ name: 'TraineeDetails', params: { id: trainee.id } }">
          {{ trainee.name }}
        </router-link>
      </li>
      <li v-if="loadingMore" class="list-group-item text-center">
        <span>جاري تحميل المزيد...</span>
      </li>
    </ul>
    <p v-else-if="!loading" class="no-results">لا توجد نتائج</p>
    <p v-else class="no-results">جاري التحميل...</p>
  </div>
</template>

<script>
export default {
  data() {
    return {
      searchQuery: '',
      trainees: [],
      selectedIndex: -1,
      loading: true,
      loadingMore: false,
      currentPage: 1,
      hasMorePages: true,
      isSearchMode: false,
    };
  },
  mounted() {
    this.loadRecentTrainees();
  },
  methods: {
    handleScroll(event) {
      // Only load more if we're in recent trainees mode (not searching)
      if (this.isSearchMode || this.loadingMore || !this.hasMorePages) {
        return;
      }

      const element = event.target;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;

      // Check if scrolled to bottom (with 50px threshold)
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        this.loadMoreTrainees();
      }
    },
    async loadRecentTrainees() {
      this.loading = true;
      this.currentPage = 1;
      this.isSearchMode = false;
      
      try {
        const response = await this.$axios.get('/trainer/trainees_paginated', {
          params: {
            page: this.currentPage
          }
        });
        
        if (response.data.trainees && response.data.trainees.data) {
          this.trainees = response.data.trainees.data;
          this.hasMorePages = response.data.trainees.current_page < response.data.trainees.last_page;
          
          if (this.trainees.length > 0) {
            this.selectedIndex = 0;
          }
        } else {
          this.trainees = [];
          this.selectedIndex = -1;
          this.hasMorePages = false;
        }
      } catch (error) {
        console.error('API error:', error);
        this.trainees = [];
        this.selectedIndex = -1;
        this.hasMorePages = false;
      } finally {
        this.loading = false;
      }
    },
    async loadMoreTrainees() {
      if (!this.hasMorePages || this.loadingMore) {
        return;
      }

      this.loadingMore = true;
      this.currentPage++;

      try {
        const response = await this.$axios.get('/trainer/trainees_paginated', {
          params: {
            page: this.currentPage
          }
        });

        if (response.data.trainees && response.data.trainees.data) {
          const newTrainees = response.data.trainees.data;
          this.hasMorePages = response.data.trainees.current_page < response.data.trainees.last_page;

          // Append new trainees to existing list
          if (newTrainees.length > 0) {
            this.trainees = [...this.trainees, ...newTrainees];
          } else {
            this.hasMorePages = false;
          }
        } else {
          this.hasMorePages = false;
        }
      } catch (error) {
        console.error('API error:', error);
        this.hasMorePages = false;
      } finally {
        this.loadingMore = false;
      }
    },
    async searchTrainees() {
      if (this.searchQuery.length === 0) {
        await this.loadRecentTrainees();
        return;
      }
      
      if (this.searchQuery.length < 2) {
        return;
      }
      
      this.loading = true;
      this.isSearchMode = true;
      this.hasMorePages = false;
      
      try {
        const response = await this.$axios.get('/trainer/search_trainee', {
          params: {
            trainee_name: this.searchQuery,
            first_age: 0,
            second_age: 0,
            trainee_id: 0,
            place: 0,
            sex: 0,
            goal: '',
            search_id: '',
          },
        });
        if (response.data.trainees && response.data.trainees.length > 0) {
          this.trainees = response.data.trainees;
          this.selectedIndex = 0;
        } else {
          this.trainees = [];
          this.selectedIndex = -1;
        }
      } catch (error) {
        console.error('API error:', error);
        this.trainees = [];
        this.selectedIndex = -1;
      } finally {
        this.loading = false;
      }
    },
    handleKeydown(event) {
      if (this.trainees.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.selectedIndex = Math.min(this.selectedIndex + 1, this.trainees.length - 1);
          this.scrollToSelected();
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
          this.scrollToSelected();
          break;
        case 'Enter':
          event.preventDefault();
          if (this.selectedIndex >= 0 && this.selectedIndex < this.trainees.length) {
            this.navigateToTrainee(this.trainees[this.selectedIndex].id);
          }
          break;
      }
    },
    scrollToSelected() {
      this.$nextTick(() => {
        const activeItem = document.querySelector('.active-item');
        if (activeItem) {
          activeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    },
    navigateToTrainee(id) {
      this.$router.push({ name: 'TraineeDetails', params: { id } });
    }
  },
};
</script>

<style scoped>
.search-container {
  max-width: 800px;
  margin: 40px auto;
  padding: 20px;
  direction: rtl;
  text-align: right;
}

.form-control {
  text-align: right;
  direction: rtl;
}

.list-group {
  max-height: 500px;
  overflow-y: auto;
}

.list-group-item {
  text-align: right;
  direction: rtl;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.list-group-item.active-item {
  background-color: #e3f2fd;
  border-color: #2196f3;
}

.list-group-item a {
  color: #007bff;
  text-decoration: none;
  display: block;
  width: 100%;
}

.list-group-item.active-item a {
  color: #1976d2;
  font-weight: 500;
}

.list-group-item a:hover {
  color: #0056b3;
  text-decoration: underline;
}

.no-results {
  text-align: center;
  color: #6c757d;
  padding: 20px;
}

.text-center {
  text-align: center;
  color: #6c757d;
  padding: 10px;
}
</style>
